import React, { CSSProperties, useEffect } from 'react';
import Slider from 'react-slider';
import { Transition } from 'react-spring/renderprops';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { useLocalStore } from '../../../../../stores';

import styles from './GenderExperience.module.scss';

type Props = {
    mode?: 'single' | 'dual',

    gender: string[],
    rating: {
        active: string[],

        age: number[],
        experience: number[],
        ntrp: number[],
        ello: number[],
    },

    onChange: (gender: string[], rating: any) => void,
};

type LocalStore = {
    gender: string[],

    sliders: [
        {
            type: 'age' | 'experience',
            slider: {
                age: {
                    min: number,
                    max: number,
                    step: number,
                    minDistance: number,
                    range: number[],
                },
                experience: {
                    min: number,
                    max: number,
                    step: number,
                    minDistance: number,
                    range: number[],
                },
            },
        },
        {
            type: 'ntrp' | 'ello',
            slider: {
                ntrp: {
                    min: number,
                    max: number,
                    step: number,
                    minDistance: number,
                    range: number[],
                },
                ello: {
                    min: number,
                    max: number,
                    step: number,
                    minDistance: number,
                    range: number[],
                },
            },
        },
    ],

    popup: {
        visible: boolean,
        range: number[],
        value: number,
    },
};

export default observer(function GenderExperience({ mode, gender, rating, onChange }: Props) {
    const { localStore } = useLocalStore<LocalStore>({
        gender: ['male', 'female'],

        sliders: [
            {
                type: 'age',
                slider: {
                    age: {
                        min: 10,
                        max: 70,
                        step: 1,
                        minDistance: 10,
                        range: [10, 70],
                    },
                    experience: {
                        min: 0,
                        max: 30,
                        step: 1,
                        minDistance: 5,
                        range: [0, 30],
                    },
                },
            },
            {
                type: 'ntrp',
                slider: {
                    ntrp: {
                        min: 1,
                        max: 7,
                        step: 0.5,
                        minDistance: 1,
                        range: [1, 7],
                    },
                    ello: {
                        min: 1000,
                        max: 3000,
                        step: 50,
                        minDistance: 300,
                        range: [1000, 3000],
                    },
                },
            },
        ],

        popup: {
            visible: false,
            range: [0, 0],
            value: 0,
        },
    });

    useEffect(() => {
        localStore.gender = gender;
    }, [gender]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        localStore.sliders[0].type = rating.active[0] || 'age';
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        localStore.sliders[1].type = rating.active[1] || 'ntrp';

        localStore.sliders[0].slider.age.range = rating.age;
        localStore.sliders[0].slider.experience.range = rating.experience;
        localStore.sliders[1].slider.ntrp.range = rating.ntrp;
        localStore.sliders[1].slider.ello.range = rating.ello;
    }, [rating]);

    const ganderHandler = (value: string) => {
        if (mode === 'single') {
            localStore.gender = [value];
        } else if (localStore.gender.includes(value)) {
            if (localStore.gender.length === 2) {
                localStore.gender = localStore.gender.filter(gender => gender !== value);
            }
        } else {
            localStore.gender.push(value);
        }

        onChange(toJS(localStore.gender), toJS({
            active: [localStore.sliders[0].type, localStore.sliders[1].type],

            age: localStore.sliders[0].slider.age.range,
            experience: localStore.sliders[0].slider.experience.range,
            ntrp: localStore.sliders[1].slider.ntrp.range,
            ello: localStore.sliders[1].slider.ello.range,
        }));
    };

    return (
        <div className={styles.container}>
            <div role="presentation" style={{ width: 80, height: 150 }} onClick={() => ganderHandler('male')}>
                <Transition
                    items={localStore.gender.includes('male')}
                    from={{ position: 'absolute', opacity: 0 }}
                    enter={{ opacity: 1 }}
                    leave={{ opacity: 0 }}
                    config={{
                        duration: 200,
                    }}
                >
                    {(toggle: boolean) => (toggle
                        ? (props: any) => (
                            <img
                                style={props}
                                src={require('../../assets/images/male-active.png').default}
                                width="80"
                                alt="Male"
                            />
                        )
                        : (props: any) => (
                            <img
                                style={props}
                                src={require('../../assets/images/male.png').default}
                                width="80"
                                alt="Male"
                            />
                        )
                    )}
                </Transition>
            </div>

            <div className={styles.slidersContainer}>
                <div className={styles.slidersType} style={{ marginBottom: 15 }}>
                    {localStore.popup.visible && (
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                width: '100%',
                                height: '100%',
                                textAlign: 'center',
                                backgroundColor: '#ffffff',
                                zIndex: 10,
                            }}
                        >
                            Set to <strong>{localStore.popup.value}</strong>
                        </div>
                    )}

                    <div
                        role="presentation"
                        className={classNames(
                            styles.slidersTypeItem,
                            {
                                [styles.slidersTypeItemActive]: localStore.sliders[0].type === 'age',
                            },
                        )}
                        onClick={() => {
                            localStore.sliders[0].type = 'age';

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    >
                        Age
                    </div>

                    <div
                        role="presentation"
                        className={classNames(
                            styles.slidersTypeItem,
                            {
                                [styles.slidersTypeItemActive]: localStore.sliders[0].type === 'experience',
                            },
                        )}
                        onClick={() => {
                            localStore.sliders[0].type = 'experience';

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    >
                        Experience
                    </div>
                </div>

                <div>
                    <Slider
                        className={styles.slider}
                        thumbClassName={styles.sliderThumb}
                        trackClassName={styles.sliderTrack}
                        renderThumb={(props, state) => (
                            <div {...props}>
                                {state.valueNow}{state.valueNow >= localStore.sliders[0].slider[localStore.sliders[0].type].max ? '+' : ''}
                            </div>
                        )}
                        min={localStore.sliders[0].slider[localStore.sliders[0].type].min}
                        max={localStore.sliders[0].slider[localStore.sliders[0].type].max}
                        step={localStore.sliders[0].slider[localStore.sliders[0].type].step}
                        minDistance={localStore.sliders[0].slider[localStore.sliders[0].type].minDistance}
                        value={mode === 'single'
                            ? localStore.sliders[0].slider[localStore.sliders[0].type].range[0]
                            : localStore.sliders[0].slider[localStore.sliders[0].type].range}
                        onBeforeChange={() => {
                            localStore.popup.visible = true;
                        }}
                        onChange={(range) => {
                            range = range
                                ? Array.isArray(range) ? range : [range, range]
                                : [
                                    localStore.sliders[0].slider[localStore.sliders[0].type].min,
                                    localStore.sliders[0].slider[localStore.sliders[0].type].max,
                                ];

                            if (localStore.popup.range[0] !== range[0]) {
                                localStore.popup.value = range[0];
                            }
                            if (localStore.popup.range[1] !== range[1]) {
                                localStore.popup.value = range[1];
                            }
                            localStore.popup.range = range;

                            localStore.sliders[0].slider[localStore.sliders[0].type].range = range;
                        }}
                        onAfterChange={() => {
                            localStore.popup.visible = false;

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    />

                    <Slider
                        className={styles.slider}
                        thumbClassName={styles.sliderThumb}
                        trackClassName={styles.sliderTrack}
                        renderThumb={(props, state) => <div {...props}>{state.valueNow}</div>}
                        min={localStore.sliders[1].slider[localStore.sliders[1].type].min}
                        max={localStore.sliders[1].slider[localStore.sliders[1].type].max}
                        step={localStore.sliders[1].slider[localStore.sliders[1].type].step}
                        minDistance={localStore.sliders[1].slider[localStore.sliders[1].type].minDistance}
                        value={mode === 'single'
                            ? localStore.sliders[1].slider[localStore.sliders[1].type].range[0]
                            : localStore.sliders[1].slider[localStore.sliders[1].type].range}
                        onBeforeChange={(range) => {
                            localStore.popup.visible = true;
                        }}
                        onChange={(range) => {
                            range = range
                                ? Array.isArray(range) ? range : [range, range]
                                : [
                                    localStore.sliders[1].slider[localStore.sliders[1].type].min,
                                    localStore.sliders[1].slider[localStore.sliders[1].type].max,
                                ];

                            if (localStore.popup.range[0] !== range[0]) {
                                localStore.popup.value = range[0];
                            }
                            if (localStore.popup.range[1] !== range[1]) {
                                localStore.popup.value = range[1];
                            }
                            localStore.popup.range = range;

                            localStore.sliders[1].slider[localStore.sliders[1].type].range = range;
                        }}
                        onAfterChange={() => {
                            localStore.popup.visible = false;

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    />
                </div>

                <div className={styles.slidersType}>
                    <div
                        role="presentation"
                        className={classNames(
                            styles.slidersTypeItem,
                            {
                                [styles.slidersTypeItemActive]: localStore.sliders[1].type === 'ntrp',
                            },
                        )}
                        onClick={() => {
                            localStore.sliders[1].type = 'ntrp';

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    >
                        NTRP
                    </div>

                    <div
                        role="presentation"
                        className={classNames(
                            styles.slidersTypeItem,
                            {
                                [styles.slidersTypeItemActive]: localStore.sliders[1].type === 'ello',
                            },
                        )}
                        onClick={() => {
                            localStore.sliders[1].type = 'ello';

                            onChange(toJS(localStore.gender), toJS({
                                active: [localStore.sliders[0].type, localStore.sliders[1].type],

                                age: localStore.sliders[0].slider.age.range,
                                experience: localStore.sliders[0].slider.experience.range,
                                ntrp: localStore.sliders[1].slider.ntrp.range,
                                ello: localStore.sliders[1].slider.ello.range,
                            }));
                        }}
                    >
                        ELLO
                    </div>
                </div>
            </div>

            <div role="presentation" style={{ width: 80, height: 150 }} onClick={() => ganderHandler('female')}>
                <Transition
                    items={localStore.gender.includes('female')}
                    from={{ position: 'absolute', opacity: 0 }}
                    enter={{ opacity: 1 }}
                    leave={{ opacity: 0 }}
                >
                    {(toggle: boolean) => (toggle
                            ? (props: any) => <img
                                style={props} src={require('../../assets/images/female-active.png').default} width="80"
                                alt="Female"
                            />
                            : (props: any) => (
                                <img
                                    style={props}
                                    src={require('../../assets/images/female.png').default}
                                    width="80"
                                    alt="Female"
                                />
                            )
                    )}
                </Transition>
            </div>
        </div>
    );
});
